// Sprint 6: очередь tool_call'ов в режиме confirm.
// Оператор жмёт «Выполнить» → replay через PipelineService.executeConfirmedToolCall + audit.
// Или «Отклонить» с причиной. TTL 30 мин → status='expired' (cron-джоба, пока dev-хук).

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckIcon, ClockCountdownIcon, HourglassLowIcon, XIcon } from '@phosphor-icons/react';
import {
  executePendingAction,
  fetchPendingActions,
  rejectPendingAction,
  type PendingActionView,
} from '@/features/autonomy/api';
import { Button, Card, CardContent, CardHeader, CardTitle, EmptyState, PageHeader, SkeletonRows, StatusChip, cn, fmtRelativeTime, type Tone } from '@smena/ui';

const STATUS_TONE: Record<PendingActionView['status'], Tone> = {
  pending: 'amber',
  executed: 'green',
  rejected: 'muted',
  expired: 'red',
};

const STATUS_LABEL: Record<PendingActionView['status'], string> = {
  pending: 'ждёт',
  executed: 'выполнено',
  rejected: 'отклонено',
  expired: 'просрочено',
};

export function PendingActionsPage() {
  const [filter, setFilter] = useState<PendingActionView['status'] | 'all'>('pending');
  const q = useQuery({
    queryKey: ['pending-actions', filter],
    queryFn: () => fetchPendingActions(filter === 'all' ? undefined : filter),
    refetchInterval: 5_000,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between">
        <div>
          <PageHeader
            icon={<ClockCountdownIcon weight="bold" />}
            title="История подтверждений"
            description="Что Smena спрашивала и чем закончилось. Всё, что ждёт вас прямо сейчас, лежит в разделе «Внимание»."
          />
        </div>
        <div className="flex gap-1 text-xs">
          {(['pending', 'executed', 'rejected', 'expired', 'all'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={cn(
                'rounded-md border px-2.5 py-1 transition-colors',
                filter === f ? 'border-accent-fg bg-accent/15 text-accent-fg' : 'border-line hover:bg-panel2',
              )}
            >
              {f === 'all' ? 'все' : STATUS_LABEL[f]}
            </button>
          ))}
        </div>
      </div>

      {q.isLoading ? (
        <Card>
          <SkeletonRows rows={3} />
        </Card>
      ) : !q.data || q.data.items.length === 0 ? (
        <Card>
          <EmptyState
            tone="good"
            icon={<CheckIcon weight="bold" />}
            title="Ничего не ждёт вашей кнопки"
            description="Сюда попадают действия, для которых вы включили «Спросит вас» - например вызов мастера."
          />
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {q.data.items.map((item) => (
            <PendingCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function PendingCard({ item }: { item: PendingActionView }) {
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const executeMut = useMutation({
    mutationFn: () => executePendingAction(item.id),
    onSuccess: (r) => {
      toast.success(`Выполнено: ${r.toolName}`);
      void qc.invalidateQueries({ queryKey: ['pending-actions'] });
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  const rejectMut = useMutation({
    mutationFn: () => rejectPendingAction(item.id, reason.trim() || undefined),
    onSuccess: () => {
      toast.success('Отклонено');
      setRejecting(false);
      setReason('');
      void qc.invalidateQueries({ queryKey: ['pending-actions'] });
    },
    onError: (e: Error) => toast.error(`Ошибка: ${e.message}`),
  });

  const isPending = item.status === 'pending';
  const argsPreview = safeStringify(item.toolCall);
  const ttlMs = new Date(item.expiresAt).getTime() - Date.now();
  const ttlPositive = ttlMs > 0;

  return (
    <Card className={cn('border-l-4', isPending ? 'border-l-amber' : 'border-l-line')}>
      <CardContent className="pt-5">
        <div className="mb-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <StatusChip tone={STATUS_TONE[item.status]}>{STATUS_LABEL[item.status]}</StatusChip>
            <code className="rounded bg-panel2/60 px-2 py-0.5 text-xs">{item.toolName}</code>
            <span className="text-[11px] text-muted">
              sid: <code>{item.sid}</code>
            </span>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-muted">
            {isPending ? (
              <span
                className={cn('inline-flex items-center gap-1', ttlPositive ? 'text-amber' : 'text-red')}
                title={`истекает ${new Date(item.expiresAt).toLocaleString('ru-RU')}`}
              >
                {ttlPositive ? <ClockCountdownIcon weight="bold" className="size-3" /> : <HourglassLowIcon weight="bold" className="size-3" />}
                {ttlPositive ? `осталось ${humanTtl(ttlMs)}` : 'просрочено'}
              </span>
            ) : null}
            <span>{fmtRelativeTime(item.createdAt)}</span>
          </div>
        </div>

        <pre className="whitespace-pre-wrap break-words rounded-md bg-panel2/60 p-3 text-xs">{argsPreview}</pre>

        {item.reason ? (
          <div className="mt-2 text-xs text-muted">
            <b>Результат:</b> {item.reason}
          </div>
        ) : null}

        {isPending ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {rejecting ? (
              <>
                <input
                  type="text"
                  autoFocus
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="причина (необязательно)"
                  className="flex-1 min-w-40 rounded-md border border-line-control bg-input px-2 py-1 text-sm"
                />
                <Button size="sm" onClick={() => rejectMut.mutate()} disabled={rejectMut.isPending}>
                  <XIcon weight="bold" /> Отклонить
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setRejecting(false)}>
                  Отмена
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" onClick={() => executeMut.mutate()} disabled={executeMut.isPending}>
                  <CheckIcon weight="bold" /> Выполнить
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setRejecting(true)}>
                  <XIcon weight="bold" /> Отклонить
                </Button>
              </>
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function humanTtl(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}с`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}м ${s % 60}с`;
  const h = Math.floor(m / 60);
  return `${h}ч ${m % 60}м`;
}
